import { identifyUserTraits } from 'lib/analytics';
import LoginWrapper from 'pages/login/LoginWrapper';
import { fiveMinutes } from 'queries/constants';
import { useCompleteOnboarding } from 'queries/hooks/useCompleteOnboarding';
import { useWorkspaces } from 'queries/hooks/useWorkspaces';
import { workspaceQueryKeys } from 'queries/queryKeys/workspaceKeys';
import { useState } from 'react';
import { useQueryClient } from 'react-query';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { List } from 'services/WorkspaceService';
import { useTheme } from 'ui/hooks/useTheme';
import { OnboardingProgress } from './OnboardingProgress';
import { OrganizationNameStep } from './OrganizationNameStep';
import { RoleStep } from './RoleStep';
import { roleDisplayNames, type OnboardingRole } from './roles';
import { useShouldShowOnboarding } from './useShouldShowOnboarding';

const totalSteps = 2;

/**
 * First-run flow for a user who has just signed themselves up, shown in place of the app until
 * they finish it.
 *
 * The role they pick is sent to analytics as a `role` trait, which the data sources page reads to
 * suggest data sources. Finishing records onboarding as complete, along with the organization name
 * if they gave one, and takes them to the data sources page for their first workspace.
 *
 * A user who shouldn't be onboarded, such as one whose tenant already has been, is sent to the app,
 * unless the URL has `?force=true`. Finishing a forced flow still records completion, which renames
 * the tenant if it was never onboarded and a name is given, and changes nothing if it already was.
 */
export const Onboarding: React.FC = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const completeOnboarding = useCompleteOnboarding();
    const { isLoading: isTenantLoading, shouldShowOnboarding } = useShouldShowOnboarding();
    const [searchParams] = useSearchParams();
    const isForced = searchParams.get('force') === 'true';

    /**
     * Onboarding renders outside the app wrapper that normally applies the theme, so it's applied here
     * to follow the tenant's setting (the system theme for a new tenant) rather than the default dark.
     */
    const theme = useTheme();

    /** Loaded up front so that finishing doesn't usually have to wait for it */
    useWorkspaces();

    const [step, setStep] = useState(1);
    const [isFinishing, setFinishing] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string>();

    const returnToStep = (previousStep: number) => {
        setErrorMessage(undefined);
        setStep(previousStep);
    };

    const selectRole = (selectedRole: OnboardingRole) => {
        identifyUserTraits({ role: roleDisplayNames[selectedRole] });
        setStep(2);
    };

    /**
     * Completion, which also saves the organization name, is awaited before leaving because the
     * route guard would otherwise still see an incomplete tenant and redirect straight back here.
     */
    const finishOnboarding = async (organizationName?: string) => {
        setFinishing(true);
        setErrorMessage(undefined);

        /**
         * Waits for the workspaces query, which may still be loading or may have failed, rather than
         * reading whatever it held when the user finished. Started alongside completion so a refetch
         * doesn't wait behind it. The app is a fine fallback if it can't load.
         */
        const workspacesRequest = queryClient
            .fetchQuery(workspaceQueryKeys.list, List, { staleTime: fiveMinutes })
            .catch(() => undefined);

        try {
            await completeOnboarding.mutateAsync(organizationName);
        } catch {
            setErrorMessage('Something went wrong. Please try again.');
            setFinishing(false);
            return;
        }

        const workspaces = await workspacesRequest;
        const workspaceId = workspaces?.[0]?.id;

        navigate(workspaceId ? `/datasources/${workspaceId}` : '/', { replace: true });
    };

    if (isTenantLoading) {
        return null;
    }

    /**
     * Finishing marks the cached tenant complete before onboarding hands off to the data sources page,
     * so the redirect is held back while finishing to avoid sending the user to the app root first.
     */
    if (!shouldShowOnboarding && !isFinishing && !isForced) {
        return <Navigate to='/' replace />;
    }

    return (
        <LoginWrapper className='max-w-3xl my-auto' theme={theme} linkLogo={false}>
            <div className='flex flex-col flex-1 min-h-0' data-testid='onboardingPage'>
                <OnboardingProgress currentStep={step} totalSteps={totalSteps} onStepSelect={returnToStep} />

                {step === 1 && <RoleStep onSelect={selectRole} />}

                {step === 2 && (
                    <OrganizationNameStep
                        onSubmit={finishOnboarding}
                        onSkip={() => finishOnboarding()}
                        isSaving={isFinishing}
                    />
                )}

                {errorMessage && (
                    <p role='alert' className='mt-6 text-statusErrorPrimary' data-testid='onboardingError'>
                        {errorMessage}
                    </p>
                )}
            </div>
        </LoginWrapper>
    );
};
